import styled, { css, keyframes } from 'styled-components';
import { screenMd } from 'constants/styles/breakpoints';
import { colors } from 'constants/styles/colors';
import { compactCheckbox } from 'components/checkbox/styles';
import { compactSwitch } from 'components/switch/styles';

const spin = keyframes`
0% {
    transform:rotate(360deg);
}
`;

export const Container = styled.div`
  padding: 30px 10px 0px 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${screenMd} {
    padding: 0;
  }
  /* The design's 30px between the last section and "Copy invite link". */
  > section:last-of-type {
    margin-bottom: 0;
  }
  > button {
    margin-top: 32px;
    align-self: end !important;
    font-weight: 700;
    ${screenMd} {
      margin-top: 30px;
    }
  }
  button {
    font-weight: 700;
  }
`;

export const Section = styled.section`
  margin-bottom: 40px;
`;

export const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 900;
  line-height: 26px;
  color: ${colors.dataVisualization.black.dark};
  margin: 0 0 3px 0;
`;

export const SectionSubtitle = styled.h4`
  font-size: 16px;
  font-weight: 900;
  line-height: 23px;
  color: ${colors.fonts.dark};
  margin: 0 0 12px 0;
`;

export const SectionDescription = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
  margin: 0 0 12px 0;
`;

export const OptionalLabel = styled.p`
  font-size: 14px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
  margin: -8px 0 12px 0;
`;

export const InviteLinkInput = styled.div`
  border-radius: 10px;
  min-height: 58px;
  padding: 10px;
  font-size: 12px;
  background: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyLighter};
  color: ${colors.dataVisualization.black.dark};
  display: flex;
  align-items: center;
  gap: 10px;
  word-break: break-word;
  ${screenMd} {
    width: 100%;
    min-height: 44px;
    line-height: 14px;
  }
  > span {
    flex: 1;
  }
  > svg {
    margin-left: auto;
    min-width: 25px;
    animation: ${spin} 2s infinite linear;
  }
`;

// `$alignTop` keeps the toggle level with the first line when the label has a second line under it.
export const ToggleRow = styled.div<{ $alignTop?: boolean }>`
  display: flex;
  align-items: ${({ $alignTop }) => ($alignTop ? 'flex-start' : 'center')};
  gap: 10px;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
  ${compactSwitch}
  & + & {
    margin-top: 12px;
  }
  b {
    font-size: 12px;
    line-height: 14px;
    color: ${colors.fonts.dark};
  }
`;

// The label, with anything shown under it lined up with the label's text rather than the toggle.
export const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
`;

export const NotOnPlanQualifier = styled.i`
  color: ${colors.fonts.mutedGray};
`;

export const GetSkillDiveLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.palette.Business_DataVizLilacDark};
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  text-align: left;
`;

export const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 12px;
`;

// Team icon colours are keys of the data-visualization palette; an unknown key falls back to grey.
// The `66` alpha byte is the 40% tint the chips are drawn with.
const chipBackground = (color?: string) =>
  `${colors.dataVisualization[color]?.lighter ?? colors.dataVisualization.gray.dusty}66`;

export const Chip = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 24px;
  padding: 4px 7px 5px 8px;
  border-radius: 30px;
  font-size: 12px;
  line-height: 14px;
  /* 70% of the text colour, as drawn. */
  color: ${colors.dataVisualization.black.dark}B3;
  background: ${({ $color }) => chipBackground($color)};
  > button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: inherit;
    display: inline-flex;
    align-items: center;
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

export const TeamPickerField = styled.div<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${colors.palette.Business_LightThemeGreyLighter};
  cursor: text;
  ${({ $open }) =>
    $open &&
    css`
      box-shadow: 0 0 0 2px ${colors.palette.Business_DataVizLilacLighter}4D,
        inset 0 0 0 1px ${colors.palette.Business_DataVizLilacLighter};
    `}
  &:focus-within {
    box-shadow: 0 0 0 2px ${colors.palette.Business_DataVizLilacLighter}4D,
      inset 0 0 0 1px ${colors.palette.Business_DataVizLilacLighter};
  }
  > svg {
    flex-shrink: 0;
  }
`;

export const TeamPickerInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  line-height: 14px;
  color: ${colors.dataVisualization.black.dark};
  &::placeholder {
    color: ${colors.palette.Business_LightThemeGreyDarker};
  }
`;

export const TeamPickerPanel = styled.div`
  padding: 20px;
  border-radius: 10px;
  background: ${colors.fonts.white};
  box-shadow: 0 30px 24px rgba(0, 0, 0, 0.2);
`;

export const TeamOptionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 272px;
  overflow-y: auto;
  /* Room for a row's hover fill, which reaches past the rows; the margin keeps them where drawn. */
  margin: -6px 0 -6px -8px;
  padding: 6px 10px 6px 8px;
  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: ${colors.dataVisualization.gray.dark};
  }
  /* Just the rounded bar: no arrow buttons or track, which Windows draws otherwise. */
  &::-webkit-scrollbar-button {
    display: none;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const TeamOption = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-shrink: 0;
  position: relative;
  isolation: isolate;
  /* As drawn, the first team sits flush under the panel's padding. */
  padding: 11px 0 12px;
  &:first-child {
    padding-top: 0;
  }
  border-bottom: 1px solid ${colors.palette.Business_LightThemeGreyLighter};
  /* The design has no hover state; the fill pads the row evenly without moving its content. */
  &::before {
    content: '';
    position: absolute;
    inset: 5px -8px 7px;
    z-index: -1;
    border-radius: 6px;
  }
  &:first-child::before {
    top: -6px;
  }
  &:hover::before,
  &:focus-within::before {
    background: ${colors.palette.Business_LightThemeGreyLighter};
  }
  ${compactCheckbox}
  /* The tick box's label, holding the team's icon and name, fills the row up to the toggle. */
  > .ant-checkbox-wrapper {
    flex: 1;
    min-width: 0;
    > span:not(.ant-checkbox) {
      display: flex;
      flex: 1;
      min-width: 0;
      padding: 0 0 0 15px;
      > span {
        display: flex;
        flex: 1;
        min-width: 0;
      }
    }
  }
`;

export const TeamOptionName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${colors.fonts.dark};
`;

export const ManagerToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 0 10px;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
  cursor: default;
  ${compactSwitch}
`;

// The team link's headings use the larger title style, with the gap of a subtitle.
export const TeamLinkHeading = styled(SectionTitle)`
  margin-bottom: 12px;
`;

export const NoTeamsFound = styled.p`
  margin: 0;
  padding: 8px 0;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
  text-align: center;
`;

// A team row's icon and name, inside its tick box's label.
export const TeamOptionLabel = styled.span`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 15px;
  min-width: 0;
`;
